import { type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { AuthStatus, Permission, ProjectRole } from '@k-ucap/shared';
import { useAuthStore } from '@/stores/useAuthStore.js';
import { AccessDeniedPage } from './AccessDeniedPage.js';
import { LoadingScreen } from './LoadingScreen.js';

export const AuthenticatedRoute = ({ children }: { children: ReactNode }) => {
  const status = useAuthStore((s) => s.authStatus);
  if (status === AuthStatus.INITIALIZING || status === AuthStatus.PROFILE_LOADING) return <LoadingScreen />;
  if (status === AuthStatus.UNAUTHENTICATED || status === AuthStatus.ERROR || status === AuthStatus.ACCESS_DENIED) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
};

export const GuestOnlyRoute = ({ children }: { children: ReactNode }) => {
  const status = useAuthStore((s) => s.authStatus);
  if (status === AuthStatus.INITIALIZING || status === AuthStatus.PROFILE_LOADING) return <LoadingScreen />;
  if (status === AuthStatus.READY) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

export const PermissionProtectedRoute = ({ permission, children }: { permission: Permission; children: ReactNode }) => {
  const status = useAuthStore((s) => s.authStatus);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  if (status === AuthStatus.INITIALIZING || status === AuthStatus.PROFILE_LOADING) return <LoadingScreen />;
  if (status !== AuthStatus.READY) return <Navigate to="/login" replace />;
  if (!hasPermission(permission)) return <AccessDeniedPage requiredPermission={permission} />;
  return <>{children}</>;
};

export const ProjectAccessRoute = ({
  projectId,
  role,
  children,
}: {
  projectId?: string;
  role?: ProjectRole;
  children: ReactNode;
}) => {
  const status = useAuthStore((s) => s.authStatus);
  const selectedProject = useAuthStore((s) => s.selectedProject);
  const canAccess = useAuthStore((s) => s.canAccessProject);
  const targetProjectId = projectId ?? selectedProject?.id;
  if (status === AuthStatus.INITIALIZING || status === AuthStatus.PROFILE_LOADING) return <LoadingScreen />;
  if (status !== AuthStatus.READY) return <Navigate to="/login" replace />;
  if (!targetProjectId || !canAccess(targetProjectId, role)) return <AccessDeniedPage />;
  return <>{children}</>;
};
